import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Save, RefreshCcw, Percent, Tag, Info, TrendingUp, Search, UploadCloud, Eye, EyeOff, RefreshCw, Layers, RotateCcw, Lock, Share2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  PRICE_LIST_MODE_FIXED,
  PRICE_LIST_MODE_PERCENTAGE,
  PRICE_LIST_MODE_BULK,
  PRICE_LIST_BASE_COST,
  PRICE_LIST_BASE_LIST,
  getPriceListUiMode,
  getPriceListProductPrices,
  getPriceListProductRules,
  getPriceListHiddenProductIds,
  getPriceListSummaryLabel,
  getProductBasePrice,
  getProductCostPrice,
  getEffectiveRule,
  roundMoney,
  computeRulePrice,
  impliedPercentage,
  normalizeBulkRule,
  isBulkPriceList,
  toPosPricingProduct,
} from '../utils/priceLists';
import { fetchAllRows } from '../utils/supabasePagination';
import { isProductSelectable } from '../utils/productStatus';
import PriceListImportModal from '../components/PriceListImportModal';
import PriceListShareModal from '../components/PriceListShareModal';

const moneyAr = (value) =>
  `$ ${Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DEFAULT_BULK_RULE = { base: PRICE_LIST_BASE_LIST, pct: 0, dynamic: true, apply_to_new: true };

function ListasPreciosForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile, organization, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);
  // Todos los productos (inactivos y archivados incluidos) para guardar la
  // lista completa; `products` son los que se muestran (activos).
  const [allProducts, setAllProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [overwriteManual, setOverwriteManual] = useState(false);
  // La lista cargada ya era masiva: al pasarla a otro modo hay que limpiar
  // bulk_rule/product_rules en la base.
  const [loadedAsBulk, setLoadedAsBulk] = useState(false);
  // Regla masiva tal como se cargó: si no cambió, los renglones "fijos" ya
  // guardados conservan su precio congelado.
  const [loadedBulkKey, setLoadedBulkKey] = useState(null);
  // Lista vieja de "% global" que se abrió: se muestra como masiva y se
  // convierte recién al guardar.
  const [convertedFromPercentage, setConvertedFromPercentage] = useState(false);

  const [form, setForm] = useState({
    name: '',
    pricing_mode: PRICE_LIST_MODE_BULK,
    adjustment_percentage: 0,
    product_prices: {},
    bulk_rule: { ...DEFAULT_BULK_RULE },
    product_rules: {},
    // ✅ Set de IDs ocultos. Lo guardamos como Set para performance,
    //    al persistir lo volcamos a un array.
    hidden_product_ids: new Set(),
    auto_sync_with_base_price: false,
    is_active: true,
  });

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadProducts();
    if (id) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile?.organization_id]);

  const loadProducts = async () => {
    try {
      const { data, error } = await fetchAllRows(
        () => supabase
          .from('products')
          .select('id, name, barcode, sell_price, cost_price, brand, priced_in_usd, price_usd, active, is_active, archived_at, product_variants(price)')
          .eq('organization_id', profile.organization_id),
        { label: 'listasPreciosProductos' }
      );

      if (error) throw error;
      const sorted = (data || []).slice().sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'es'));
      setAllProducts(sorted);
    } catch (error) {
      console.error(error);
      setAllProducts([]);
      toast.error('No se pudo cargar el catalogo de productos');
    }
  };

  // Base de precio igual a la del POS (USD convertido, sin variantes).
  const usdRate = organization?.usd_exchange_rate;
  const pricingProducts = useMemo(
    () => allProducts.map((p) => toPosPricingProduct(p, usdRate)),
    [allProducts, usdRate]
  );
  const products = useMemo(() => pricingProducts.filter(isProductSelectable), [pricingProducts]);

  const loadData = async () => {
    setInitialLoading(true);
    try {
      const { data, error } = await supabase
        .from('price_lists')
        .select('*')
        .eq('id', id)
        .eq('organization_id', profile.organization_id)
        .single();

      if (error) throw error;

      const loadedIsBulk = isBulkPriceList(data);
      const uiMode = getPriceListUiMode(data);
      const isLegacyPercentage = uiMode === PRICE_LIST_MODE_PERCENTAGE;
      setLoadedAsBulk(loadedIsBulk);
      if (loadedIsBulk) {
        const r = normalizeBulkRule(data.bulk_rule);
        setLoadedBulkKey(JSON.stringify({ base: r.base, pct: r.pct, dynamic: r.dynamic }));
      } else {
        setLoadedBulkKey(null);
      }
      setConvertedFromPercentage(isLegacyPercentage);
      setForm({
        name: data?.name || '',
        // El "% global" viejo se edita como el modo masivo (mismo resultado:
        // % sobre precio de lista que varía), y se convierte al guardar.
        pricing_mode: isLegacyPercentage ? PRICE_LIST_MODE_BULK : uiMode,
        adjustment_percentage: Number(data?.adjustment_percentage || 0),
        product_prices: isLegacyPercentage ? {} : getPriceListProductPrices(data),
        bulk_rule: loadedIsBulk
          ? normalizeBulkRule(data.bulk_rule)
          : { ...DEFAULT_BULK_RULE, pct: isLegacyPercentage ? Number(data?.adjustment_percentage || 0) : 0 },
        product_rules: isLegacyPercentage ? {} : getPriceListProductRules(data),
        hidden_product_ids: new Set(getPriceListHiddenProductIds(data).map(String)),
        auto_sync_with_base_price: data?.auto_sync_with_base_price === true,
        is_active: data?.is_active !== false,
      });
    } catch (error) {
      console.error(error);
      toast.error('Error al recuperar parametros');
      navigate('/sistema/listas-precios');
    } finally {
      setInitialLoading(false);
    }
  };

  const bulk = useMemo(() => normalizeBulkRule(form.bulk_rule), [form.bulk_rule]);
  // apply_to_new no cambia precios: no cuenta como cambio de regla.
  const ruleKey = (rule) => JSON.stringify({ base: rule.base, pct: rule.pct, dynamic: rule.dynamic });
  const bulkUnchanged = loadedBulkKey !== null && ruleKey(bulk) === loadedBulkKey;

  const examplePrice = useMemo(() => 1000 * (1 + (Number(bulk.pct) || 0) / 100), [bulk.pct]);

  /**
   * Precio que va a tener un renglón en la lista masiva.
   * - manual: el $ cargado a mano.
   * - varía: se calcula con el costo / precio actual.
   * - fijo: conserva el $ ya guardado mientras su regla no cambie.
   * price null = no hay base para calcular (sin costo): se vende al precio base.
   */
  const rowPricing = (product) => {
    const key = String(product.id);
    const rule = getEffectiveRule(key, form.product_rules, bulk);
    const stored = form.product_prices?.[key];
    const hasStored = stored !== undefined && stored !== null && stored !== '' && Number.isFinite(Number(stored));
    let price;
    if (rule.manual) {
      price = hasStored ? Number(stored) : null;
    } else if (rule.dynamic) {
      price = computeRulePrice(product, rule);
    } else {
      const keepStored = hasStored && (rule.custom || (bulkUnchanged && !!form.product_rules?.[key]));
      price = keepStored ? Number(stored) : computeRulePrice(product, rule);
    }
    // Sin base para calcular (ej. costo en 0): conserva el último $ guardado,
    // igual que el recalculo de la base.
    if (price == null && !rule.manual && hasStored) price = Number(stored);
    return { rule, price };
  };

  /** Reglas y $ finales de todos los productos cargados, listos para guardar. */
  const materializeBulk = () => {
    const rules = { ...(form.product_rules || {}) };
    const prices = { ...(form.product_prices || {}) };
    pricingProducts.forEach((product) => {
      const key = String(product.id);
      const { rule, price } = rowPricing(product);
      const pct = Number(rule.pct);
      rules[key] = {
        base: rule.base === PRICE_LIST_BASE_COST ? PRICE_LIST_BASE_COST : PRICE_LIST_BASE_LIST,
        pct: Number.isFinite(pct) ? pct : 0,
        dynamic: !rule.manual && rule.dynamic !== false,
        manual: !!rule.manual,
        custom: !!(rule.custom || rule.manual),
      };
      if (price == null) delete prices[key];
      else prices[key] = roundMoney(price);
    });
    return { product_rules: rules, product_prices: prices };
  };

  const ruleStats = useMemo(() => {
    if (form.pricing_mode !== PRICE_LIST_MODE_BULK) return { custom: 0, manual: 0, noPrice: 0, belowCost: 0 };
    let custom = 0;
    let manual = 0;
    let noPrice = 0;
    let belowCost = 0;
    products.forEach((product) => {
      const { rule, price } = rowPricing(product);
      if (rule.manual) manual += 1;
      else if (rule.custom) custom += 1;
      if (price == null) noPrice += 1;
      const cost = getProductCostPrice(product);
      if (price != null && cost > 0 && price < cost) belowCost += 1;
    });
    return { custom, manual, noPrice, belowCost };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pricing_mode, form.product_rules, form.product_prices, bulk, products, bulkUnchanged]);

  const specificPriceCount = useMemo(() => {
    return Object.values(form.product_prices || {}).filter((value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) && numericValue >= 0;
    }).length;
  }, [form.product_prices]);

  const filteredProducts = useMemo(() => {
    const query = String(productSearch || '').trim().toLowerCase();
    if (!query) return products;

    return products.filter((product) =>
      [product?.name, product?.brand, product?.barcode]
        .some((value) => String(value || '').toLowerCase().includes(query))
    );
  }, [products, productSearch]);

  const handleModeChange = (mode) => {
    if (mode === form.pricing_mode) return;

    if (mode === PRICE_LIST_MODE_FIXED) {
      // Se conservan los $ que mostraba el modo masivo como precios fijos.
      const { product_prices } = materializeBulk();
      setForm((current) => ({ ...current, pricing_mode: mode, product_prices, product_rules: {}, adjustment_percentage: 0 }));
      return;
    }

    // A masivo: los precios fijos cargados pasan como renglones manuales.
    setForm((current) => {
      const keptPrices = current.product_prices || {};
      const productById = new Map(pricingProducts.map((p) => [String(p.id), p]));
      const rules = {};
      Object.entries(keptPrices).forEach(([pid, price]) => {
        rules[pid] = {
          base: PRICE_LIST_BASE_LIST,
          pct: impliedPercentage(price, getProductBasePrice(productById.get(String(pid)))) ?? 0,
          dynamic: false,
          manual: true,
          custom: true,
        };
      });
      return {
        ...current,
        pricing_mode: mode,
        bulk_rule: { ...DEFAULT_BULK_RULE, ...(current.bulk_rule || {}) },
        product_prices: { ...keptPrices },
        product_rules: rules,
        auto_sync_with_base_price: false,
      };
    });
  };

  const setBulkRule = (patch) => {
    setForm((current) => ({ ...current, bulk_rule: { ...(current.bulk_rule || DEFAULT_BULK_RULE), ...patch } }));
  };

  const [filteredPct, setFilteredPct] = useState('');

  /** Aplica un % propio a los productos filtrados (quedan personalizados). */
  const applyPctToProducts = (targetProducts, pctValue) => {
    const pct = Number(pctValue);
    if (pctValue === '' || !Number.isFinite(pct)) return toast.error('Ingresá el % a aplicar');
    if (pct <= -100) return toast.error('El % no puede ser -100 o menos');
    const rules = { ...(form.product_rules || {}) };
    const prices = { ...(form.product_prices || {}) };
    let applied = 0;
    let skippedManual = 0;
    targetProducts.forEach((product) => {
      const key = String(product.id);
      if (rules[key]?.manual && !overwriteManual) { skippedManual += 1; return; }
      rules[key] = { base: bulk.base, pct, dynamic: bulk.dynamic, manual: false, custom: true };
      delete prices[key];
      applied += 1;
    });
    setForm((current) => ({ ...current, product_rules: rules, product_prices: prices }));
    toast.success(`${pct}% aplicado a ${applied} producto${applied === 1 ? '' : 's'}${skippedManual ? ` · ${skippedManual} con precio manual se respetaron` : ''}. Guardá para confirmar.`);
    return null;
  };

  /** Vuelve productos a seguir el % general (quita sus ajustes propios). */
  const resetProductsToBulk = (targetProducts) => {
    const rules = { ...(form.product_rules || {}) };
    const prices = { ...(form.product_prices || {}) };
    let reset = 0;
    targetProducts.forEach((product) => {
      const key = String(product.id);
      const rule = rules[key];
      if (!rule || !(rule.custom || rule.manual)) return;
      if (rule.manual && !overwriteManual) return;
      delete rules[key];
      delete prices[key];
      reset += 1;
    });
    setForm((current) => ({ ...current, product_rules: rules, product_prices: prices }));
    toast.success(`${reset} producto${reset === 1 ? '' : 's'} vuelven al % general`);
  };

  /** Cambia la regla de un renglón (%, base o varía/fijo): queda personalizado. */
  const updateRowRule = (product, patch) => {
    const key = String(product.id);
    setForm((current) => {
      const prev = getEffectiveRule(key, current.product_rules, current.bulk_rule);
      const baseRule = { base: prev.base, pct: prev.pct, dynamic: prev.manual ? bulk.dynamic : prev.dynamic };
      if (prev.manual && patch.pct === undefined) {
        const baseValue = (patch.base ?? baseRule.base) === PRICE_LIST_BASE_COST ? getProductCostPrice(product) : getProductBasePrice(product);
        baseRule.pct = impliedPercentage(current.product_prices?.[key], baseValue) ?? baseRule.pct;
      }
      const rules = { ...(current.product_rules || {}), [key]: { ...baseRule, ...patch, manual: false, custom: true } };
      const prices = { ...(current.product_prices || {}) };
      delete prices[key];
      return { ...current, product_rules: rules, product_prices: prices };
    });
  };

  /** $ cargado a mano: el renglón queda manual y nunca se recalcula solo. Vacío = vuelve al %. */
  const updateRowManualPrice = (product, rawValue) => {
    const key = String(product.id);
    setForm((current) => {
      const rules = { ...(current.product_rules || {}) };
      const prices = { ...(current.product_prices || {}) };
      const numericValue = Number(rawValue);
      if (rawValue === '' || !Number.isFinite(numericValue) || numericValue < 0) {
        delete rules[key];
        delete prices[key];
      } else {
        const prev = getEffectiveRule(key, current.product_rules, current.bulk_rule);
        rules[key] = { base: prev.base, pct: prev.pct, dynamic: false, manual: true, custom: true };
        prices[key] = rawValue;
      }
      return { ...current, product_rules: rules, product_prices: prices };
    });
  };

  const resetRowToBulk = (product) => {
    const key = String(product.id);
    setForm((current) => {
      const rules = { ...(current.product_rules || {}) };
      const prices = { ...(current.product_prices || {}) };
      delete rules[key];
      delete prices[key];
      return { ...current, product_rules: rules, product_prices: prices };
    });
  };

  // Lista "como se ve ahora" (borrador) para imprimir / WhatsApp.
  const buildDraftPriceList = () => {
    const isBulk = form.pricing_mode === PRICE_LIST_MODE_BULK;
    const materialized = isBulk ? materializeBulk() : null;
    return {
      id: id || null,
      name: form.name,
      pricing_mode: PRICE_LIST_MODE_FIXED,
      adjustment_percentage: 0,
      product_prices: isBulk ? materialized.product_prices : (form.product_prices || {}),
      bulk_rule: isBulk ? bulk : null,
      product_rules: isBulk ? materialized.product_rules : {},
      hidden_product_ids: Array.from(form.hidden_product_ids || []).map(String),
    };
  };
  const [shareSnapshot, setShareSnapshot] = useState(null);
  const openShare = () => {
    setShareSnapshot(buildDraftPriceList());
    setIsShareOpen(true);
  };

  const isProductHidden = (productId) =>
    form.hidden_product_ids instanceof Set
      ? form.hidden_product_ids.has(String(productId))
      : false;

  const toggleProductHidden = (productId) => {
    setForm((current) => {
      const nextSet = new Set(current.hidden_product_ids || []);
      const key = String(productId);
      if (nextSet.has(key)) nextSet.delete(key);
      else nextSet.add(key);
      return { ...current, hidden_product_ids: nextSet };
    });
  };

  const setAllProductsVisible = () => {
    setForm((current) => ({ ...current, hidden_product_ids: new Set() }));
  };

  const hiddenCount = useMemo(() => {
    return form.hidden_product_ids instanceof Set ? form.hidden_product_ids.size : 0;
  }, [form.hidden_product_ids]);

  const updateSpecificPrice = (productId, nextValue) => {
    setForm((current) => {
      const nextPrices = { ...(current.product_prices || {}) };
      const numericValue = Number(nextValue);

      if (nextValue === '' || !Number.isFinite(numericValue) || numericValue < 0) {
        delete nextPrices[productId];
      } else {
        nextPrices[productId] = numericValue;
      }

      return {
        ...current,
        product_prices: nextPrices,
      };
    });
  };

  const applyImportedPrices = (nextProductPrices, successfulLogs) => {
    setForm((current) => ({
      ...current,
      pricing_mode: PRICE_LIST_MODE_FIXED,
      product_prices: nextProductPrices,
    }));

    const importedCount = Array.isArray(successfulLogs)
      ? new Set(successfulLogs.map((log) => log?.productId).filter(Boolean)).size
      : 0;
    toast.success(
      importedCount > 0
        ? `Se cargaron ${importedCount} precios al borrador. Guarda la lista para confirmarlo.`
        : 'Los precios importados quedaron listos en el borrador.'
    );
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!profile?.organization_id) return toast.error('Sesion invalida');

    const name = String(form.name || '').trim();
    if (!name) return toast.error('Completa el nombre de la lista');

    setLoading(true);
    try {
      const hiddenArray = Array.from(form.hidden_product_ids || []).map(String);

      const isBulk = form.pricing_mode === PRICE_LIST_MODE_BULK;
      const isFixedLike = isBulk || form.pricing_mode === PRICE_LIST_MODE_FIXED;

      if (isBulk) {
        const generalPct = Number(form.bulk_rule?.pct);
        if (form.bulk_rule?.pct === '' || !Number.isFinite(generalPct)) {
          setLoading(false);
          return toast.error('Completá el % general (puede ser 0)');
        }
        const invalidRow = Object.values(form.product_rules || {}).some((rule) => {
          if (!rule || rule.manual) return false;
          const v = Number(rule.pct);
          return rule.pct === '' || !Number.isFinite(v) || v <= -100;
        });
        if (generalPct <= -100 || invalidRow) {
          setLoading(false);
          return toast.error('Hay un % vacío o de -100 o menos: dejaría el precio en $0');
        }
      }

      const payload = {
        name: name.toUpperCase(),
        pricing_mode: isFixedLike ? PRICE_LIST_MODE_FIXED : PRICE_LIST_MODE_PERCENTAGE,
        adjustment_percentage: form.pricing_mode === PRICE_LIST_MODE_PERCENTAGE ? Number(form.adjustment_percentage || 0) : 0,
        product_prices: isFixedLike ? getPriceListProductPrices(form) : {},
      };
      const materialized = isBulk ? materializeBulk() : null;
      if (isBulk) payload.product_prices = materialized.product_prices;
      Object.assign(payload, {
        hidden_product_ids: hiddenArray,
        auto_sync_with_base_price: form.pricing_mode === PRICE_LIST_MODE_FIXED && !!form.auto_sync_with_base_price,
        is_active: !!form.is_active,
        organization_id: profile.organization_id,
      });
      // Columnas del modo masivo: solo se mandan si aplican, así las listas
      // clásicas se guardan igual que siempre.
      if (isBulk) {
        payload.bulk_rule = bulk;
        payload.product_rules = materialized.product_rules;
      } else if (loadedAsBulk) {
        payload.bulk_rule = null;
        payload.product_rules = {};
      }

      let savedId = id;
      if (id) {
        const { error } = await supabase
          .from('price_lists')
          .update(payload)
          .eq('id', id)
          .eq('organization_id', profile.organization_id);

        if (error) throw error;
        toast.success('PARAMETROS ACTUALIZADOS');
      } else {
        const { data: inserted, error } = await supabase.from('price_lists').insert([payload]).select('id').single();
        if (error) throw error;
        savedId = inserted?.id;
        toast.success('NUEVA LISTA REGISTRADA');
      }

      // Los renglones "varía" se recalculan en la base con costo/precio frescos.
      if (isBulk && savedId) {
        const { error: recalcError } = await supabase.rpc('price_list_recalc', { p_list: savedId });
        if (recalcError) console.warn('price_list_recalc', recalcError);
      }

      navigate('/sistema/listas-precios');
    } catch (error) {
      console.error(error);
      toast.error('Error tecnico al guardar');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || initialLoading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">
          Sincronizando Formulario...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/sistema/listas-precios')}
            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 shadow-sm hover:text-[#51A7FD] transition-all active:scale-95"
            title="Volver"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Percent size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">
              {id ? 'Editar Lista' : 'Nueva Lista'}
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Define porcentaje global o precios especificos por producto
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openShare}
            className="bg-white border border-slate-200 text-slate-600 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm hover:text-[#0B2A36] hover:border-slate-300 transition-all"
            title="Imprimir o mandar por WhatsApp lo que ves (incluye cambios sin guardar)"
          >
            <Share2 size={16} /> Imprimir / WhatsApp
          </button>
          <button
            onClick={guardar}
            disabled={loading}
            className="bg-[#3ff2aa] text-[#0B2A36] px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
            title="Guardar"
          >
            {loading ? <RefreshCcw className="animate-spin" size={16} /> : <Save size={16} strokeWidth={3} />}
            Guardar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Denominacion</p>
            <Tag size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-xl font-black text-slate-800 tracking-tight truncate uppercase">
            {form.name || 'SIN NOMBRE'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Nombre de la lista</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Modo Comercial</p>
            <Info size={16} className="text-slate-300" />
          </div>
          {form.pricing_mode === PRICE_LIST_MODE_BULK ? (
            <>
              <p className={`mt-1.5 text-2xl font-black tracking-tight ${Number(form.bulk_rule?.pct) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {`${Number(form.bulk_rule?.pct) >= 0 ? '+' : ''}${Number(form.bulk_rule?.pct || 0).toFixed(2)}%`}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Sobre {form.bulk_rule?.base === PRICE_LIST_BASE_COST ? 'costo' : 'precio de lista'} · {form.bulk_rule?.dynamic === false ? 'queda fijo' : 'varía'}
              </p>
            </>
          ) : (
            <>
              <p className={`mt-1.5 text-2xl font-black tracking-tight ${form.pricing_mode === PRICE_LIST_MODE_PERCENTAGE ? (Number(form.adjustment_percentage) >= 0 ? 'text-emerald-600' : 'text-rose-600') : 'text-slate-800'}`}>
                {form.pricing_mode === PRICE_LIST_MODE_PERCENTAGE
                  ? `${Number(form.adjustment_percentage) >= 0 ? '+' : ''}${Number(form.adjustment_percentage || 0).toFixed(2)}%`
                  : specificPriceCount}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                {form.pricing_mode === PRICE_LIST_MODE_PERCENTAGE ? 'Sobre precio base' : 'Precios definidos'}
              </p>
            </>
          )}
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              {form.pricing_mode === PRICE_LIST_MODE_FIXED ? 'Resumen' : 'Simulacion $1.000'}
            </p>
            <TrendingUp size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
            {form.pricing_mode === PRICE_LIST_MODE_FIXED
              ? getPriceListSummaryLabel({ ...form, bulk_rule: null })
              : `$ ${examplePrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {form.pricing_mode === PRICE_LIST_MODE_BULK
              ? `${ruleStats.custom} con % propio · ${ruleStats.manual} manual${ruleStats.manual === 1 ? '' : 'es'}`
              : form.pricing_mode === PRICE_LIST_MODE_PERCENTAGE ? 'Ejemplo de calculo' : 'Lista configurada'}
          </p>
        </div>
      </div>

      <form onSubmit={guardar} className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag size={16} className="text-[#3FF2AA]" />
              <h2 className="text-[10px] font-black text-white uppercase tracking-widest">
                Parametros Generales
              </h2>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div className="space-y-2">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Denominacion de la lista
              </label>
              <input
                type="text"
                className="w-full bg-slate-50 border border-slate-100 px-4 py-4 rounded-2xl text-[11px] font-black uppercase outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] transition-all shadow-inner"
                data-onboarding-id="price-list-name-input"
                value={form.name}
                onChange={(e) => setForm((current) => ({ ...current, name: e.target.value.toUpperCase() }))}
                required
                placeholder="EJ: MAYORISTA, CLIENTES VIP..."
              />
            </div>

            <div className="space-y-3">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Metodo de calculo
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleModeChange(PRICE_LIST_MODE_BULK)}
                  className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                    form.pricing_mode === PRICE_LIST_MODE_BULK
                      ? 'border-[#0B2A36] bg-[#0B2A36] text-white shadow-lg'
                      : 'border-slate-200 bg-slate-50 text-slate-600'
                  }`}
                >
                  <div className="text-[10px] font-black uppercase tracking-widest">1. Porcentaje</div>
                  <p className={`mt-2 text-[11px] font-bold leading-relaxed ${form.pricing_mode === PRICE_LIST_MODE_BULK ? 'text-white/80' : 'text-slate-400'}`}>
                    Un % para todos los productos, sobre el costo o el precio de lista. Despues podes ajustar cualquier renglon a mano.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => handleModeChange(PRICE_LIST_MODE_FIXED)}
                  className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                    form.pricing_mode === PRICE_LIST_MODE_FIXED
                      ? 'border-[#0B2A36] bg-[#0B2A36] text-white shadow-lg'
                      : 'border-slate-200 bg-slate-50 text-slate-600'
                  }`}
                >
                  <div className="text-[10px] font-black uppercase tracking-widest">2. Precios especificos</div>
                  <p className={`mt-2 text-[11px] font-bold leading-relaxed ${form.pricing_mode === PRICE_LIST_MODE_FIXED ? 'text-white/80' : 'text-slate-400'}`}>
                    Define un precio puntual por producto. Si no completas uno, mantiene el precio base.
                  </p>
                </button>
              </div>
            </div>

            {form.pricing_mode === PRICE_LIST_MODE_FIXED ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        Precios especificos por producto
                      </label>
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-300 ml-1 mt-2">
                        Carga solo los productos que quieras sobrescribir dentro de la lista.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsImportModalOpen(true)}
                      className="px-4 py-3 rounded-2xl bg-[#0B2A36] text-white text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-slate-800 transition-all shadow-sm"
                    >
                      <UploadCloud size={16} />
                      Importar lista
                    </button>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="BUSCAR PRODUCTO..."
                      className="w-full bg-slate-50 border border-slate-100 pl-10 pr-4 py-3 rounded-2xl text-[11px] font-black uppercase outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] transition-all shadow-inner"
                    />
                  </div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-blue-400 ml-1">
                    La importacion carga el borrador con Excel o PDF y despues necesitas guardar la lista.
                  </p>
                </div>

                <div
                  className={`rounded-2xl border p-4 flex items-start gap-3 transition-colors ${
                    form.auto_sync_with_base_price
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div
                    className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${
                      form.auto_sync_with_base_price
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-white text-slate-400 border border-slate-200'
                    }`}
                  >
                    <RefreshCw size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <label htmlFor="auto-sync-toggle" className="text-[10px] font-black uppercase tracking-widest text-slate-700 cursor-pointer">
                        Auto-sincronizar con precio base
                      </label>
                      <button
                        type="button"
                        id="auto-sync-toggle"
                        onClick={() => setForm((current) => ({ ...current, auto_sync_with_base_price: !current.auto_sync_with_base_price }))}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30 ${
                          form.auto_sync_with_base_price ? 'bg-emerald-500' : 'bg-slate-300'
                        }`}
                        role="switch"
                        aria-checked={form.auto_sync_with_base_price}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                            form.auto_sync_with_base_price ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="mt-2 text-[10px] font-bold leading-relaxed text-slate-500">
                      Cuando esta activo, si otro proceso actualiza el precio de un producto, su precio en esta lista se iguala automaticamente al nuevo. Solo se sincronizan los productos que ya tienen un precio cargado aca: los demas siguen tomando el precio base.
                    </p>
                  </div>
                </div>

                <div className="max-h-[420px] overflow-y-auto rounded-2xl border border-slate-100 divide-y divide-slate-100">
                  {filteredProducts.map((product) => {
                    const basePrice = getProductBasePrice(product);
                    const currentSpecificPrice = form.product_prices?.[product.id] ?? '';

                    return (
                      <div key={product.id} className="grid grid-cols-1 md:grid-cols-[1.4fr_0.7fr_0.9fr] gap-3 p-4 bg-white">
                        <div className="min-w-0">
                          <div className="text-[11px] font-black text-slate-800 uppercase truncate flex items-center gap-1.5 flex-wrap">
                            <span className="truncate">{product.name || 'SIN NOMBRE'}</span>
                            {product.brand && (
                              <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                                {product.brand}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-400">
                            ID: {String(product.id).slice(0, 8)}
                          </div>
                        </div>
                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                          <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Base</div>
                          <div className="mt-1 text-[11px] font-black text-slate-700">
                            $ {Number(basePrice || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                          </div>
                        </div>
                        <div>
                          <div className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1">Precio lista</div>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={currentSpecificPrice}
                            onChange={(e) => updateSpecificPrice(product.id, e.target.value)}
                            placeholder="Usar base"
                            className="w-full bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-[11px] font-black outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD]"
                          />
                        </div>
                      </div>
                    );
                  })}

                  {filteredProducts.length === 0 && (
                    <div className="px-4 py-12 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                      No hay productos para ese filtro
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {convertedFromPercentage && (
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 flex items-start gap-2 text-[10px] font-bold text-sky-800">
                    <Info size={16} className="shrink-0 mt-0.5" />
                    <p>
                      Esta lista era de % global. Ya ves el {Number(bulk.pct).toFixed(2)}% aplicado en cada renglon, igual que antes.
                      Al guardar queda con el nuevo formato y podes ajustar productos uno por uno. Si no guardas, sigue como estaba.
                    </p>
                  </div>
                )}

                {/* ── % general ─────────────────────────────────────── */}
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-[0.8fr_1.2fr_1.2fr] gap-3">
                    <div className="space-y-1">
                      <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">% general</label>
                      <div className="relative">
                        <Percent className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                        <input
                          type="number"
                          step="0.01"
                          data-onboarding-id="price-list-margin-input"
                          value={form.bulk_rule?.pct ?? 0}
                          onChange={(e) => setBulkRule({ pct: e.target.value })}
                          className="w-full bg-white border border-slate-200 pl-9 pr-3 py-3 rounded-xl text-[12px] font-black outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD]"
                        />
                      </div>
                      <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 ml-1">(+) recargo · (-) descuento</p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Calcular sobre</label>
                      <div className="grid grid-cols-2 gap-1 bg-white border border-slate-200 rounded-xl p-1">
                        {[
                          [PRICE_LIST_BASE_COST, 'Costo'],
                          [PRICE_LIST_BASE_LIST, 'Precio de lista'],
                        ].map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setBulkRule({ base: value })}
                            className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                              bulk.base === value ? 'bg-[#0B2A36] text-white' : 'text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Cuando cambie el {bulk.base === PRICE_LIST_BASE_COST ? 'costo' : 'precio'}</label>
                      <div className="grid grid-cols-2 gap-1 bg-white border border-slate-200 rounded-xl p-1">
                        {[
                          [true, 'Varía'],
                          [false, 'Queda fijo'],
                        ].map(([value, label]) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() => setBulkRule({ dynamic: value })}
                            className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                              bulk.dynamic === value ? 'bg-[#0B2A36] text-white' : 'text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className="text-[10px] font-bold leading-relaxed text-slate-500">
                    {bulk.dynamic
                      ? `Todos los productos que no ajustaste a mano siguen este %. El precio se recalcula solo cada vez que cambia el ${bulk.base === PRICE_LIST_BASE_COST ? 'costo' : 'precio de lista'} (importaciones incluidas).`
                      : `Todos los productos que no ajustaste a mano siguen este %. El precio se calcula al guardar y queda congelado aunque despues cambie el ${bulk.base === PRICE_LIST_BASE_COST ? 'costo' : 'precio de lista'}.`}
                  </p>

                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer text-[9px] font-black uppercase tracking-widest text-slate-600">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-[#51A7FD]"
                        checked={bulk.apply_to_new}
                        onChange={(e) => setBulkRule({ apply_to_new: e.target.checked })}
                      />
                      Aplicar tambien a productos nuevos
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-[9px] font-black uppercase tracking-widest text-slate-600">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-rose-500"
                        checked={overwriteManual}
                        onChange={(e) => setOverwriteManual(e.target.checked)}
                      />
                      Las acciones masivas pisan tambien los precios cargados a mano
                    </label>
                  </div>
                </div>

                {(ruleStats.noPrice > 0 || ruleStats.belowCost > 0) && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-[10px] font-bold text-amber-800">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      {ruleStats.noPrice > 0 && (
                        <p>{ruleStats.noPrice} producto{ruleStats.noPrice === 1 ? '' : 's'} sin {bulk.base === PRICE_LIST_BASE_COST ? 'costo' : 'precio'} cargado: se venden al precio base hasta que lo tengan.</p>
                      )}
                      {ruleStats.belowCost > 0 && (
                        <p>{ruleStats.belowCost} producto{ruleStats.belowCost === 1 ? '' : 's'} quedan por debajo del costo.</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="BUSCAR PRODUCTO O MARCA..."
                    className="w-full bg-slate-50 border border-slate-100 pl-10 pr-4 py-3 rounded-2xl text-[11px] font-black uppercase outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] transition-all shadow-inner"
                  />
                </div>

                {/* Acciones sobre el grupo filtrado (ej: una marca) */}
                <div className="flex flex-col md:flex-row md:items-center gap-2 justify-between">
                  {productSearch.trim() ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                        A los {filteredProducts.length} filtrados:
                      </span>
                      <div className="relative w-24">
                        <input
                          type="number"
                          step="0.01"
                          value={filteredPct}
                          onChange={(e) => setFilteredPct(e.target.value)}
                          placeholder={String(bulk.pct)}
                          className="w-full bg-white border border-slate-200 px-2 py-2 pr-6 rounded-lg text-[11px] font-black outline-none focus:border-[#51A7FD]"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-black text-slate-300">%</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => applyPctToProducts(filteredProducts, filteredPct)}
                        disabled={filteredProducts.length === 0}
                        className="px-3 py-2 rounded-lg bg-[#0B2A36] text-white text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40"
                      >
                        <Layers size={13} /> Aplicar
                      </button>
                      <button
                        type="button"
                        onClick={() => resetProductsToBulk(filteredProducts)}
                        className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5"
                      >
                        <RotateCcw size={13} /> Volver al % general
                      </button>
                    </div>
                  ) : (
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                      Tip: buscá una marca o rubro para aplicarle otro % a ese grupo
                    </span>
                  )}
                  {!productSearch.trim() && (ruleStats.custom + (overwriteManual ? ruleStats.manual : 0)) > 0 && (
                    <button
                      type="button"
                      onClick={() => resetProductsToBulk(products)}
                      className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5"
                    >
                      <RotateCcw size={13} /> Todos al % general
                    </button>
                  )}
                </div>

                {/* ── Renglones ────────────────────────────────────── */}
                <div className="max-h-[520px] overflow-y-auto rounded-2xl border border-slate-100 divide-y divide-slate-100">
                  <div className="hidden md:grid grid-cols-[1.4fr_0.6fr_0.6fr_1.5fr_0.8fr_0.5fr] gap-3 px-4 py-2 bg-slate-50 text-[8px] font-black uppercase tracking-widest text-slate-400 sticky top-0 z-10">
                    <span>Producto</span>
                    <span className="text-right">Costo</span>
                    <span className="text-right">P. lista</span>
                    <span>% del renglon</span>
                    <span className="text-right">$ Final</span>
                    <span className="text-right">Margen</span>
                  </div>
                  {filteredProducts.map((product) => {
                    const key = String(product.id);
                    const { rule, price } = rowPricing(product);
                    const cost = getProductCostPrice(product);
                    const listBase = getProductBasePrice(product);
                    const finalPrice = price == null ? listBase : price;
                    const ruleBaseValue = rule.base === PRICE_LIST_BASE_COST ? cost : listBase;
                    const shownPct = rule.manual ? impliedPercentage(price, ruleBaseValue) : rule.pct;
                    const margin = cost > 0 ? ((finalPrice / cost) - 1) * 100 : null;
                    const isOwn = rule.custom || rule.manual;
                    const manualRaw = form.product_prices?.[key];

                    return (
                      <div key={key} className={`grid grid-cols-1 md:grid-cols-[1.4fr_0.6fr_0.6fr_1.5fr_0.8fr_0.5fr] gap-2 md:gap-3 p-3 md:px-4 items-center ${rule.manual ? 'bg-amber-50/40' : rule.custom ? 'bg-sky-50/30' : 'bg-white'}`}>
                        <div className="min-w-0">
                          <div className="text-[11px] font-black text-slate-800 uppercase truncate flex items-center gap-1.5">
                            <span className="truncate">{product.name || 'SIN NOMBRE'}</span>
                            {product.brand && (
                              <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                                {product.brand}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-[8px] font-black uppercase tracking-widest">
                            {rule.manual && <span className="text-amber-600 inline-flex items-center gap-1"><Lock size={9} /> Precio manual</span>}
                            {!rule.manual && rule.custom && <span className="text-sky-600">% propio · {rule.dynamic ? 'varía' : 'fijo'}</span>}
                            {!isOwn && <span className="text-slate-300">Sigue el % general</span>}
                          </div>
                        </div>

                        <div className="text-[10px] font-bold text-slate-500 md:text-right">
                          <span className="md:hidden text-[8px] font-black uppercase text-slate-300 mr-1">Costo</span>
                          {cost > 0 ? moneyAr(cost) : <span className="text-slate-300">—</span>}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 md:text-right">
                          <span className="md:hidden text-[8px] font-black uppercase text-slate-300 mr-1">Lista</span>
                          {moneyAr(listBase)}
                        </div>

                        <div className="flex items-center gap-1.5">
                          <div className="relative w-20 shrink-0">
                            <input
                              type="number"
                              step="0.01"
                              value={shownPct ?? ''}
                              onChange={(e) => updateRowRule(product, { pct: e.target.value })}
                              className={`w-full bg-slate-50 border px-2 py-1.5 pr-5 rounded-lg text-[11px] font-black outline-none focus:border-[#51A7FD] ${
                                rule.manual ? 'border-amber-200 italic text-amber-700' : isOwn ? 'border-sky-200 text-sky-800' : 'border-slate-100 text-slate-500'
                              }`}
                              title={rule.manual ? '% implícito del precio manual. Si lo cambiás, el renglón vuelve a calcularse por %.' : '% de este producto'}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-black text-slate-300">%</span>
                          </div>
                          <select
                            value={rule.base}
                            onChange={(e) => updateRowRule(product, { base: e.target.value })}
                            className="bg-slate-50 border border-slate-100 rounded-lg px-1.5 py-1.5 text-[9px] font-black uppercase outline-none"
                            title="Base de cálculo"
                          >
                            <option value={PRICE_LIST_BASE_COST}>s/ costo</option>
                            <option value={PRICE_LIST_BASE_LIST}>s/ lista</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => updateRowRule(product, { dynamic: !(rule.dynamic && !rule.manual) })}
                            className={`px-2 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest border ${
                              rule.dynamic && !rule.manual ? 'bg-sky-50 text-sky-700 border-sky-100' : 'bg-slate-50 text-slate-500 border-slate-200'
                            }`}
                            title="Varía con el costo/precio o queda fijo"
                          >
                            {rule.dynamic && !rule.manual ? 'Varía' : 'Fijo'}
                          </button>
                          {isOwn && (
                            <button
                              type="button"
                              onClick={() => resetRowToBulk(product)}
                              className="p-1.5 rounded-lg text-slate-300 hover:text-[#51A7FD]"
                              title="Volver al % general"
                            >
                              <RotateCcw size={13} />
                            </button>
                          )}
                        </div>

                        <div>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={rule.manual ? (manualRaw ?? '') : (price == null ? '' : price)}
                            placeholder={price == null ? (rule.base === PRICE_LIST_BASE_COST ? 'Sin costo' : 'Sin precio') : ''}
                            onChange={(e) => updateRowManualPrice(product, e.target.value)}
                            className={`w-full md:text-right bg-white border px-2 py-1.5 rounded-lg text-[12px] font-black outline-none focus:border-[#51A7FD] ${
                              price == null ? 'border-amber-300' : rule.manual ? 'border-amber-200' : 'border-slate-200'
                            } text-slate-900`}
                            title="Escribí un precio para fijarlo a mano. Borralo para volver al %."
                          />
                        </div>

                        <div className={`text-[10px] font-black md:text-right ${margin == null ? 'text-slate-300' : margin < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                          <span className="md:hidden text-[8px] font-black uppercase text-slate-300 mr-1">Margen</span>
                          {margin == null ? '—' : `${margin.toFixed(1)}%`}
                        </div>
                      </div>
                    );
                  })}

                  {filteredProducts.length === 0 && (
                    <div className="px-4 py-12 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                      No hay productos para ese filtro
                    </div>
                  )}
                </div>
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer w-fit p-2 hover:bg-slate-50 rounded-xl transition-colors">
              <input
                type="checkbox"
                className="w-5 h-5 rounded-lg border-slate-200 text-[#51A7FD] cursor-pointer accent-[#51A7FD]"
                checked={form.is_active}
                onChange={(e) => setForm((current) => ({ ...current, is_active: e.target.checked }))}
              />
              <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">
                Lista habilitada para facturacion
              </span>
            </label>
          </div>
        </div>

        {/* ✅ SECCIÓN: Visibilidad de productos en POS / portal cliente */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye size={16} className="text-[#3FF2AA]" />
              <h2 className="text-[10px] font-black text-white uppercase tracking-widest">
                Visibilidad de productos
              </h2>
            </div>
            <span className="text-[9px] font-black uppercase tracking-widest text-white/70">
              {hiddenCount === 0 ? 'Todos visibles' : `${hiddenCount} oculto${hiddenCount === 1 ? '' : 's'}`}
            </span>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-blue-50/60 p-3 rounded-2xl border border-blue-100 flex items-start gap-3">
              <Info size={16} className="text-[#51A7FD] mt-0.5" />
              <div className="text-[9px] text-blue-700 font-black uppercase tracking-widest leading-snug">
                <p>
                  Por defecto todos los productos están VISIBLES en esta lista. Tocá el ojo para ocultar uno.
                </p>
                <p className="mt-1 text-blue-600/80">
                  Los productos ocultos no aparecen en el POS de los sitios que usen esta lista, ni en la tienda online o portal de los clientes con esta lista activa.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="BUSCAR PRODUCTO PARA OCULTAR/MOSTRAR..."
                  className="w-full bg-slate-50 border border-slate-100 pl-10 pr-4 py-3 rounded-2xl text-[11px] font-black uppercase outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] transition-all shadow-inner"
                />
              </div>

              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={setAllProductsVisible}
                  className="px-4 py-3 rounded-2xl bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-colors"
                  title="Volver a mostrar todos los productos en esta lista"
                >
                  Mostrar todos
                </button>
              )}
            </div>

            <div className="max-h-[420px] overflow-y-auto rounded-2xl border border-slate-100 divide-y divide-slate-100">
              {filteredProducts.map((product) => {
                const hidden = isProductHidden(product.id);
                return (
                  <div
                    key={product.id}
                    className={`flex items-center justify-between gap-3 p-3 transition-colors ${hidden ? 'bg-rose-50/40' : 'bg-white'}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[11px] font-black text-slate-800 uppercase truncate flex items-center gap-1.5 flex-wrap">
                        <span className="truncate">{product.name || 'SIN NOMBRE'}</span>
                        {product.brand && (
                          <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                            {product.brand}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[9px] font-black uppercase tracking-widest text-slate-400">
                        {hidden ? 'Oculto en esta lista' : 'Visible en POS y portal cliente'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleProductHidden(product.id)}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all ${
                        hidden
                          ? 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-100'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100'
                      }`}
                      title={hidden ? 'Mostrar en POS / portal' : 'Ocultar de POS / portal'}
                    >
                      {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                      {hidden ? 'Oculto' : 'Visible'}
                    </button>
                  </div>
                );
              })}

              {filteredProducts.length === 0 && (
                <div className="px-4 py-12 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                  No hay productos para ese filtro
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pb-10">
          <button
            data-onboarding-id="price-list-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#3ff2aa] text-[#0B2A36] px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50"
          >
            {loading ? <RefreshCcw className="animate-spin" size={16} /> : <Save size={16} strokeWidth={3} />}
            Guardar cambios
          </button>
        </div>
      </form>

      <PriceListShareModal
        open={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        priceList={shareSnapshot}
        products={products}
        organization={organization}
        orgId={profile?.organization_id}
      />

      <PriceListImportModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        products={allProducts.filter(isProductSelectable)}
        currentPrices={form.product_prices}
        onApplyImport={applyImportedPrices}
        priceListName={form.name}
      />
    </div>
  );
}

export default ListasPreciosForm;
